import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import type { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { router, useLocalSearchParams } from 'expo-router';
import { CircleCheck, Send as SendIcon } from 'lucide-react-native';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  TextStyle,
  View,
} from 'react-native';
import type { FlatListProps } from 'react-native';
import { Composer, GiftedChat, IMessage, InputToolbar } from 'react-native-gifted-chat';
import type { IGiftedChatContext, InputToolbarProps } from 'react-native-gifted-chat';
import type { AnimatedList } from 'react-native-gifted-chat/lib/MessageContainer/types';
import { useStore } from 'zustand';

import { ACTING_AS_BANNER_CONTENT_HEIGHT } from '@/components/ActingAsBanner';
import { ChatScreenSkeleton } from '@/components/ChatLoadingSkeleton';
import { ExpertMobileNumberSetupModal } from '@/components/ExpertMobileNumberSetupModal';
import type { ICustomMessage } from '@/components/chat';
import { ChatContentFooter } from '@/components/chat/ChatContentFooter';
import type { ChatProps } from '@/components/chat/ChatTypes';
import { ClinicalCaseJumpChip } from '@/components/chat/ClinicalCaseJumpChip';
import { ClinicalCasePeek } from '@/components/chat/ClinicalCasePeek';
import { AddReferencesUrlSheet } from '@/components/chat/actions/AddReferencesUrlSheet';
import { AttachmentAction } from '@/components/chat/actions/AttachmentAction';
import { LeaveFeedbackAction } from '@/components/chat/actions/LeaveFeedbackAction';
import { LinkToInquiryAction } from '@/components/chat/actions/LinkToInquiryAction';
import { ReferencesAction } from '@/components/chat/actions/ReferencesAction';
import { TemplateAction } from '@/components/chat/actions/TemplateAction';
import {
  createRenderMessageText,
  createRenderTime,
  renderDay,
  shouldUpdateMessageFiles,
} from '@/components/chat/chatRenderers';
import { ChatToolbar } from '@/components/chat/composer/ChatToolbar';
import { ChatToolbarSendButton } from '@/components/chat/composer/ChatToolbarSendButton';
import { ExpertRelaySendConfirm } from '@/components/chat/composer/ExpertRelaySendConfirm';
import { InquiryActionBar } from '@/components/chat/composer/InquiryActionBar';
import {
  NativeComposerDock,
  useComposerFloats,
} from '@/components/chat/composer/NativeComposerDock';
import { WebComposer } from '@/components/chat/composer/WebComposer';
import {
  BASE_INPUT_STYLES,
  COMPOSER_CORNER_RADIUS,
  composerAccessoryCornerStyle,
  composerPrimaryCornerStyle,
  NATIVE_COMPOSER_KEYBOARD_GAP,
} from '@/components/chat/composer/composerHeights';
import { DEFAULT_COMPOSER_PLACEHOLDER } from '@/components/chat/composer/composerPlaceholder';
import { useComposerPlaceholder } from '@/components/chat/composer/useComposerPlaceholder';
import { useComposerState } from '@/components/chat/composer/useComposerState';
import { useMessageTemplates } from '@/components/chat/composer/useMessageTemplates';
import { fetchConversationMessages } from '@/components/chat/fetchConversationMessages';
import type { RenderContext } from '@/components/chat/message-list/RenderContext';
import { withClinicalCase } from '@/components/chat/message-list/caseMessage';
import { makeRenderAvatar } from '@/components/chat/message-list/renderAvatar';
import { makeRenderBubble } from '@/components/chat/message-list/renderBubble';
import { makeRenderMessage } from '@/components/chat/message-list/renderMessage';
import { buildMessageLongPressActions } from '@/components/chat/messageLongPressActions';
import { parseMessage } from '@/components/chat/parseMessage';
import { ConfettiBurst } from '@/components/confetti/ConfettiBurst';
import { ComposerContextBanner } from '@/components/inbox/ComposerContextBanner';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { type MessageTemplate } from '@/constants/messageTemplates';
import { SMS_FIRST_MESSAGE_BANNER_COPY } from '@/constants/smsPromptCopy';
import { DotPhraseMenu, useDotPhraseMenu } from '@/features/dot-phrases';
import { withDotPhraseHint } from '@/features/dot-phrases/model';
import {
  OnYourHonorAction,
  OnYourHonorInlineBadge,
  OnYourHonorReasonSheet,
  useOnYourHonor,
  useOnYourHonorReasonFlow,
  useOnYourHonorReasonSurvey,
} from '@/features/on-your-honor';
import { ON_YOUR_HONOR_COPY, type OnYourHonorReasonId } from '@/features/on-your-honor/model';
import { useReferenceAssistantIntro, useStatementReferences } from '@/features/reference-assistant';
import { REFERENCE_ASSISTANT_INTRO_COPY } from '@/features/reference-assistant/model';
import { DraftReplyChip, ReplyDraftModal, useReplyDraftShortcut } from '@/features/reply-drafts';
import { useAddReferences } from '@/hooks/chat/useAddReferences';
import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import { useChatAttachments } from '@/hooks/chat/useChatAttachments';
import { useChatMessages } from '@/hooks/chat/useChatMessages';
import { useChatRealtimeSync } from '@/hooks/chat/useChatRealtimeSync';
import { useChatSendMessage } from '@/hooks/chat/useChatSendMessage';
import { useExpertFirstMessageNotification } from '@/hooks/chat/useExpertFirstMessageNotification';
import { useExpertRelaySend } from '@/hooks/chat/useExpertRelaySend';
import { useFollowListEnd } from '@/hooks/chat/useFollowListEnd';
import { useMessageRelay } from '@/hooks/chat/useMessageRelay';
import { useOutboxMessages } from '@/hooks/chat/useOutboxMessages';
import { useRevealWhenSettled } from '@/hooks/chat/useRevealWhenSettled';
import {
  useAnonymousInquiryGroupingEnabled,
  useGroupedInquiriesEnabled,
} from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { abilitySubject, useAbility } from '@/hooks/useAbility';
import useClipboard from '@/hooks/useClipboard';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useColors } from '@/hooks/useColors';
import { useComposerCalloutBanner } from '@/hooks/useComposerCalloutBanner';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useHaptic } from '@/hooks/useHaptic';
import { useIsActingAsBannerVisible } from '@/hooks/useIsActingAsBannerVisible';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { useMarkConversationAsRead } from '@/hooks/useMarkConversationAsRead';
import { useToaster } from '@/hooks/useToaster';
import { isRecommendedCaseLayout, useCaseLayout } from '@/services/chat/caseLayoutSwitch';
import {
  getCaseBox,
  isCaseBoxInView,
  isOpeningBubbleInView,
  subscribeToCaseBox,
  useClinicalCaseHeaderStore,
} from '@/services/chat/clinicalCaseHeader';
import { isReadOnlyConversation } from '@/services/chat/readOnlyInquiryConversation';
import { formatReferencesBatch } from '@/services/formatReferencesBatch';
import { useClinicalCase } from '@/services/getClinicalCase';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { getInquiryV2QueryOptions } from '@/services/getInquiryV2';
import { ADD_REFERENCES_SESSION_STATUS } from '@/services/stores/addReferencesStore';
import { CALLOUT_KEYS } from '@/services/userCallouts';
import { MESSAGE_DATA_TYPES } from '@/types/messageData';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';
import {
  trackReferenceAssistantIntroDismissed,
  trackReferenceAssistantIntroViewed,
} from '@/util/analyticsEvents';
import { getProfileFormalNameOrFallback } from '@/util/getProfileNameAndInitials';
import { isAnonymousPrimumRoutedInquiry, getAskAnonymouslySide } from '@/util/inquiryDisplay';
import { Sentry } from '@/util/sentry';
import { trackEvent } from '@/util/trackEvent';

/**
 * Chat Composer — Platform-specific layout & auto-sizing
 *
 * Behavior: ~4 visible lines at rest (100px at 24px line-height), grows
 * line-by-line up to 300px, shrinks back on delete. Action bar (attach,
 * reference, link-to-inquiry, send) sits below the text area.
 *
 * Native — uses gifted-chat's InputToolbar so it manages grow/shrink via
 * its composerHeight state. Action bar is in renderAccessory (below composer).
 * Key gotcha: textInputProps.style intentionally overrides Composer's full
 * style array — must set minHeight/maxHeight but NOT a fixed height, or
 * the TextInput won't grow.
 *
 * Web — bypasses InputToolbar (its flex-row layout centers the text input
 * between action icons). Uses WebComposer which measures scrollHeight on
 * every text change to handle both grow and shrink, since the textarea's
 * onContentSizeChange doesn't report smaller sizes on web.
 */

/** What the chat passes through to GiftedChat's message list, which is a `FlatList`. */
type MessageListProps = Partial<FlatListProps<IMessage>>;

/** Space between the list's ends and the chrome floating over them. */
const MESSAGE_LIST_EDGE_PADDING = 8;

/**
 * Space between the screen's sides and the messages and composer. On native it pads the list's
 * content, never a view around the list: the floating bar's soft edge is drawn across the scroll
 * view's own width, so an inset list draws a fade that stops short of the screen's sides.
 */
const MESSAGE_LIST_SIDE_PADDING = 10;

/**
 * Whether the message list is inverted. Only web inverts it.
 *
 * Inverting flips the scroll view with a transform, and under a transparent iOS 26 header the
 * system's scroll edge effect lands on the flipped view and blurs the whole thread, so native
 * lists run oldest first and follow their end instead, as the support thread does. Web keeps
 * the inverted list because react-native-web ignores `maintainVisibleContentPosition`, which a
 * list that is not inverted needs to hold its place while older messages load above it.
 */
const IS_MESSAGE_LIST_INVERTED = isWeb;

/**
 * The composer input's test ID, shared with the Playwright and Maestro suites through the chat
 * selector contract. Tests find the input by it rather than by its placeholder, which changes
 * by role and by which shortcut it is teaching.
 */
const COMPOSER_INPUT_TEST_ID = 'chat-composer-input';

type MessagePageData = Awaited<ReturnType<typeof fetchConversationMessages>>;

/**
 * Chat Content Component
 *
 * @overview
 * Main chat interface component that handles message display, file uploads, and user interactions.
 * Manages real-time message updates, file attachment handling, and conversation state.
 *
 * @behavior
 * - Displays messages with infinite scroll and real-time updates via Twilio conversations
 * - Handles file uploads during message sending (not pre-upload)
 * - Manages PHI confirmation for file attachments with validation
 * - Tracks comprehensive analytics for all user interactions and file operations
 * - Provides responsive header and navigation with profile links
 * - Shows real-time status feedback during message sending and file uploads
 * - Automatically marks messages as read when conversation is focused
 * - Handles keyboard avoiding behavior for different platforms
 *
 * **File Handling:**
 * - File attachment via paperclip button with document picker integration
 * - Validates file duplicates by name matching to prevent re-uploads
 * - Enforces file size limits (25 MB per file) and count limits (3 files max)
 * - Uploads files at send time with progress tracking and S3 integration
 * - Validates PHI confirmation before sending with attachments
 * - Supports same file types and constraints as inquiry form
 * - Tracks upload success/failure analytics with comprehensive metadata
 * - Provides progress feedback: "Uploading attachments (X/Y) ..." and "Sending message..."
 * - Resets PHI confirmation when all files are removed
 *
 * @analytics
 * - ATTACHMENTS_TRIGGERED: When user clicks paperclip to add files
 * - ATTACHMENTS_REMOVED: When user removes files from attachment list
 * - FILE_UPLOAD_COMPLETED: When individual files upload successfully
 * - FILE_UPLOAD_FAILED: When file operations fail with error details
 * - MESSAGE_SENT: When message with optional files is sent successfully
 * - MESSAGE_SEND_FAILED: When message sending fails with error context
 */

type ChatContentProps = ChatProps & {
  isAuthor: boolean;
  closedConversation: boolean;
  draftMessage: string;
  setDraftMessage: (message: string) => void;
  clearDraft: () => Promise<void>;
  keyboardVerticalOffset?: number;
  /**
   * Native only: the height of the chrome floating over the top of the message list, the
   * transparent header and anything pinned beneath it. The list scrolls under it and pads its
   * first message clear of it.
   */
  topInset?: number;
  /**
   * Native only: the height of the chrome over the bottom of the screen, the tab bar and the
   * home indicator. A host passes it only where the composer floats, so the messages scroll
   * under that chrome too; the composer rides above it and the list pads its end clear of it.
   */
  bottomInset?: number;
  /**
   * True while the selected grouped tab's conversation is still loading and the
   * rendered conversation is therefore the previously selected sibling. Sending
   * in that window would address the wrong thread, silently, so the composer is
   * held shut until the two agree.
   */
  isResolvingActiveConversation?: boolean;
};

export const ChatContent = memo(function ChatContent({
  userId,
  recipient,
  conversation,
  conversationStatus,
  inquiry,
  closedConversation,
  isAuthor,
  draftMessage,
  setDraftMessage,
  clearDraft,
  keyboardVerticalOffset,
  topInset = 0,
  bottomInset = 0,
  isResolvingActiveConversation = false,
}: ChatContentProps) {
  const toast = useToaster();

  /**
   * True when Twilio refused the reader and the chat fell back to reading the
   * inquiry through our API, which happens only for staff acting as Primum
   * Support on an inquiry they are not part of. A reply would post as Primum
   * Support into a conversation it is not in, and marking it read would move
   * the participants' read state, so the composer, the inquiry actions and the
   * read receipt are all withheld.
   *
   * Taken from the conversation rather than from the inquiry's participant
   * ids, which would miss a support account Twilio does let send, and an
   * attributed submitter, who reads legitimately without being either one.
   */
  const isObserver = isReadOnlyConversation(conversation);

  const queryClient = useQueryClient();

  const haptic = useHaptic();

  const isMaintenanceMode = useMaintenanceMode();

  const isInboxGroupedInquiriesEnabled = useGroupedInquiriesEnabled();

  const {
    isKeyboardVisible,
    minComposerHeight: resolvedMinComposerHeight,
    maxComposerHeight,
  } = useComposerState();

  const isActingAsBannerVisible = useIsActingAsBannerVisible();

  const { isPrimumUser } = useCognitoGroups();

  const bannerHeight = isActingAsBannerVisible ? ACTING_AS_BANNER_CONTENT_HEIGHT : 0;

  const { copyToClipboard } = useClipboard();

  const debugChatMessages = useFeatureFlag(FEATURE_FLAGS.DEBUG_CHAT_MESSAGES);

  const ability = useAbility();

  /**
   * Separate gate for the post-mark reason survey, so it can roll out to a
   * monitored subset while marking itself is live for everyone. When off,
   * marking still works but the reason sheet never opens.
   */
  const isOnYourHonorReasonEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_ON_YOUR_HONOR_REASON);

  /**
   * Expert-only inquiry action, gated by role, the expert having sent their
   * first reply (`firstRespondedAt` — there's no extra effort to attest
   * before they've responded), and the server-driven `attest` ability on this
   * inquiry (`GET /v2/users/me`'s CASL rules, scoped to the recipient and to
   * an already-answered inquiry). The ability check carries the real gate —
   * `onYourHonorAttestation` is a per-user grant, defaulted on for experts —
   * so this is advisory client-side gating; the server remains the real
   * enforcement point on the PATCH that persists it.
   */
  const canMarkOnYourHonor =
    !isAuthor &&
    inquiry.firstRespondedAt != null &&
    ability.can('attest', abilitySubject('Inquiry', inquiry));

  /**
   * Backs the reference-assistant intro callout: whether an answer currently
   * offers the add-references affordance, and a handler that opens the citation
   * wizard (automated search) for that message.
   */
  const referenceAssistantEligible = !isAuthor && inquiry.firstRespondedAt != null;

  const {
    hasReviewableReferences: hasReferenceAssistantReferences,
    openWizard: openReferenceAssistantWizard,
  } = useReferenceAssistantIntro({
    conversationId: conversation.id,
    enabled: referenceAssistantEligible,
  });

  /**
   * Eligibility for the reference-assistant intro callout: an expert who has
   * responded, and only while Primum has references ready to review on one of
   * their answers — the moment the in-chat "Select references" button appears,
   * which the callout's button reliably opens. Earning the honorarium bonus is
   * tracked server-side on publish, not here.
   */
  const canSeeReferenceAssistantIntro =
    referenceAssistantEligible && hasReferenceAssistantReferences;

  const {
    isConfirmed: isOnYourHonorConfirmed,
    toggle: toggleOnYourHonor,
    acknowledgeSent: acknowledgeOnYourHonorSent,
    trackTriggered: trackOnYourHonorTriggered,
    trackIntroViewed: trackOnYourHonorIntroViewed,
    trackIntroDismissed: trackOnYourHonorIntroDismissed,
  } = useOnYourHonor({
    conversationId: conversation.id,
    confirmed: inquiry.title.onYourHonor ?? false,
    inquiryId: inquiry.id,
    recipientId: recipient?.id,
  });

  /** Logged-in expert's name, woven into the On your honor thank-you line. */
  const { data: currentUser } = useQuery(getCurrentUserV2QueryOptions(userId));

  const onYourHonorSalutation = getProfileFormalNameOrFallback(currentUser, '');

  const {
    shouldPromptReason: shouldPromptOnYourHonorReason,
    trackReasonViewed: trackOnYourHonorReasonViewed,
    trackReasonDismissed: trackOnYourHonorReasonDismissed,
    submitReasonChip: submitOnYourHonorReasonChip,
    submitReasonCustom: submitOnYourHonorReasonCustom,
  } = useOnYourHonorReasonSurvey({
    conversationId: conversation.id,
    inquiryId: inquiry.id,
    recipientId: recipient?.id,
    enabled: canMarkOnYourHonor && isOnYourHonorReasonEnabled,
  });

  /**
   * Applies the mark and returns the rotating gratitude line to render, or null
   * when the toggle didn't land. Split from the thanking so the caller chooses
   * where that lands: the reason sheet renders it in place, while a mark with no
   * sheet has nowhere to put it and falls back to a toast.
   */
  const applyOnYourHonorMark = useCallback((): string | null => {
    const { enabled, thanksLine } = toggleOnYourHonor(onYourHonorSalutation);

    if (!enabled) {
      return null;
    }

    haptic.success();

    return thanksLine ?? null;
  }, [toggleOnYourHonor, onYourHonorSalutation, haptic]);

  /**
   * Thanks by toast, for marks that never open the sheet: cohort off, inquiry
   * already answered, or a re-mark. Without it those marks land silently, since
   * the in-sheet thank-you never runs for them.
   */
  const showOnYourHonorThanksToast = useCallback(
    (message: string) => {
      toast.showInfo({ title: message, icon: CircleCheck, liveRegion: 'polite', duration: 5000 });
    },
    [toast]
  );

  const {
    isSheetOpen: isReasonSheetOpen,
    thanksLine: reasonThanksLine,
    pressUnmarked: pressOnYourHonorUnmarked,
    selectReason: handleOnYourHonorReasonChip,
    customReason: handleOnYourHonorReasonCustom,
    closeSheet: handleCloseReasonSheet,
  } = useOnYourHonorReasonFlow({
    isReasonEnabled: isOnYourHonorReasonEnabled,
    shouldPromptReason: shouldPromptOnYourHonorReason,
    applyMark: applyOnYourHonorMark,
    showThanksToast: showOnYourHonorThanksToast,
    trackReasonViewed: trackOnYourHonorReasonViewed,
    trackReasonDismissed: trackOnYourHonorReasonDismissed,
    submitReasonChip: submitOnYourHonorReasonChip,
    submitReasonCustom: submitOnYourHonorReasonCustom,
  });

  /**
   * Pressing a marked inquiry unmarks it outright — the sheet captures why an
   * answer took extra effort, it does not guard the reversal. Everything else
   * belongs to the reason flow.
   */
  const handleOnYourHonorPress = useCallback(() => {
    trackOnYourHonorTriggered(isOnYourHonorConfirmed);

    if (isOnYourHonorConfirmed) {
      toggleOnYourHonor();

      return;
    }

    pressOnYourHonorUnmarked();
  }, [
    isOnYourHonorConfirmed,
    toggleOnYourHonor,
    pressOnYourHonorUnmarked,
    trackOnYourHonorTriggered,
  ]);

  /**
   * Template "Leave feedback" inquiry action — a scaffold behind a flag that is
   * off in every environment, so it never shows in production.
   */
  const isLeaveFeedbackEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_LEAVE_FEEDBACK);

  /**
   * No behavior wired yet — deliberately not even a toast — until the real
   * feedback flow is built.
   */
  const handleLeaveFeedback = useCallback(() => {}, []);

  /**
   * Coordinates the one-time composer intro callouts so only one shows at a time,
   * newest first: the reference-assistant announcement outranks the older
   * On-your-honor intro. Both promise a doubled honorarium, so surfacing two at
   * once would read as a double bonus — the coordinator guarantees the expert
   * sees just one. Each is gated on its own server-backed callout, so dismissing
   * resolves it and the dismissal follows the expert across devices.
   */
  const { activeKey: activeComposerCallout, dismiss: dismissActiveComposerCallout } =
    useComposerCalloutBanner([
      {
        key: CALLOUT_KEYS.EXPERT_REFERENCE_ASSISTANT_INTRO,
        enabled: canSeeReferenceAssistantIntro,
      },
      { key: CALLOUT_KEYS.EXPERT_ON_YOUR_HONOR_INTRO, enabled: canMarkOnYourHonor },
    ]);

  const showOnYourHonorIntro = activeComposerCallout === CALLOUT_KEYS.EXPERT_ON_YOUR_HONOR_INTRO;

  const showReferenceAssistantIntro =
    activeComposerCallout === CALLOUT_KEYS.EXPERT_REFERENCE_ASSISTANT_INTRO;

  /** Per-inquiry/per-expert context stamped on every reference-assistant callout event. */
  const referenceAssistantAnalyticsContext = useMemo(
    () => ({
      conversationId: conversation.id,
      inquiryId: inquiry.id,
      recipientId: recipient?.id,
    }),
    [conversation.id, inquiry.id, recipient?.id]
  );

  /**
   * Guards the intro-viewed event so it fires once when the banner first
   * appears, not again if a callouts refetch toggles visibility off then back
   * on within the session.
   */
  const hasTrackedOnYourHonorIntroView = useRef(false);

  useEffect(() => {
    if (showOnYourHonorIntro && !hasTrackedOnYourHonorIntroView.current) {
      hasTrackedOnYourHonorIntroView.current = true;

      trackOnYourHonorIntroViewed();
    }
  }, [showOnYourHonorIntro, trackOnYourHonorIntroViewed]);

  /** Dismissing the intro resolves the active callout and records the dismissal. */
  const handleDismissOnYourHonorIntro = useCallback(() => {
    trackOnYourHonorIntroDismissed();

    dismissActiveComposerCallout();
  }, [trackOnYourHonorIntroDismissed, dismissActiveComposerCallout]);

  const isAnonymousInquiryGroupingEnabled = useAnonymousInquiryGroupingEnabled();

  const params = useLocalSearchParams<{
    id?: string;
    markReadOnOpen?: string;
    siblingConversationIds?: string;
  }>();

  const { markAsRead } = useMarkConversationAsRead({
    conversation,
    conversationStatus,
    autoMarkOnView: false,
  });

  const scrollViewRef = useRef<AnimatedList<IMessage> | null>(null);

  const { hasNextPage, fetchNextPage, isFetchingNextPage, data, isLoading } = useInfiniteQuery<
    MessagePageData,
    Error,
    InfiniteData<MessagePageData>,
    string[],
    number
  >({
    queryKey: ['chat', conversation.id],
    queryFn: ({ pageParam }) =>
      fetchConversationMessages(conversation, recipient, pageParam === 0 ? undefined : pageParam),
    getNextPageParam: (lastPage) => {
      const nextPageParam = lastPage.messages.at(-1)?._id;

      return lastPage.hasPreviousPage && typeof nextPageParam === 'number'
        ? nextPageParam
        : undefined;
    },
    initialPageParam: 0,
    enabled: !!conversation,
  });

  const {
    isCovered: isLoadingCovered,
    coverOpacity: loadingCoverOpacity,
    isFading: isLoadingCoverFading,
    notifyLayoutChange: notifyListLayoutChange,
  } = useRevealWhenSettled(isLoading);

  const messages = useChatMessages({
    data,
    currentUserId: userId,
    isSubmitter: isAuthor,
  });

  const clinicalCase = useClinicalCase(conversation.id, inquiry.clinicalCase);

  const outboxMessages = useOutboxMessages(conversation.id, messages, userId);

  const casedMessages = useMemo(
    () => withClinicalCase([...outboxMessages, ...messages], clinicalCase, hasNextPage),
    [outboxMessages, messages, clinicalCase, hasNextPage]
  );

  const hasOpeningMessage = casedMessages.some(
    (message) => !!(message as { clinicalCase?: unknown }).clinicalCase
  );

  const [isOpeningVisible, setIsOpeningVisible] = useState(true);

  const caseLayout = useCaseLayout();

  const listViewportRef = useRef<View | null>(null);

  /**
   * Measures the registered node against the list. The current layout registers the case box, so
   * a message taller than the screen is not "in view" while the box is cut off. The recommended
   * layout registers the whole opening bubble and asks whether any of it shows. A box with no view is one the list
   * has not drawn or has recycled, which is off screen either way.
   */
  const measureCaseBox = useCallback(() => {
    const box = getCaseBox(conversation.id);

    const viewport = listViewportRef.current;

    if (!box || !viewport) {
      setIsOpeningVisible(false);

      return;
    }

    viewport.measureInWindow((_viewportX, viewportY, _viewportWidth, viewportHeight) => {
      box.measureInWindow((_boxX, boxY, _boxWidth, boxHeight) => {
        const isInView = isRecommendedCaseLayout(caseLayout)
          ? isOpeningBubbleInView
          : isCaseBoxInView;

        setIsOpeningVisible(
          isInView(
            { top: boxY, bottom: boxY + boxHeight },
            { top: viewportY, bottom: viewportY + viewportHeight },
            topInset
          )
        );
      });
    });
  }, [conversation.id, topInset, caseLayout]);

  useEffect(() => {
    if (!hasOpeningMessage) {
      return undefined;
    }

    measureCaseBox();

    return subscribeToCaseBox(conversation.id, measureCaseBox);
  }, [conversation.id, hasOpeningMessage, measureCaseBox]);

  const closePeek = useClinicalCaseHeaderStore((state) => state.closePeek);

  useEffect(() => {
    if (isOpeningVisible) {
      closePeek(conversation.id);
    }
  }, [isOpeningVisible, conversation.id, closePeek]);

  useEffect(() => () => closePeek(conversation.id), [conversation.id, closePeek]);

  const setClinicalCaseHeader = useClinicalCaseHeaderStore((state) => state.setHeader);

  const caseLine = clinicalCase?.status === 'written' ? clinicalCase.line.trim() : '';

  useEffect(() => {
    if (!caseLine) {
      setClinicalCaseHeader(null);

      return;
    }

    setClinicalCaseHeader({
      conversationId: conversation.id,
      line: caseLine,
      isOpeningVisible: hasOpeningMessage && isOpeningVisible,
      canScrollToOpening: hasOpeningMessage,
    });
  }, [caseLine, conversation.id, hasOpeningMessage, isOpeningVisible, setClinicalCaseHeader]);

  useEffect(() => () => setClinicalCaseHeader(null), [setClinicalCaseHeader]);

  const listMessages = useMemo(
    () => (IS_MESSAGE_LIST_INVERTED ? casedMessages : [...casedMessages].reverse()),
    [casedMessages]
  );

  const {
    scrollToLatest,
    scrollToStart,
    followEnd,
    listHandlers: followEndHandlers,
  } = useFollowListEnd(scrollViewRef, listMessages.length, {
    isEnabled: !IS_MESSAGE_LIST_INVERTED,
    listKey: conversation.id,
  });

  const scrollToOpeningRequest = useClinicalCaseHeaderStore((state) => state.scrollRequest);

  useEffect(() => {
    if (!scrollToOpeningRequest || scrollToOpeningRequest.conversationId !== conversation.id) {
      return;
    }

    // The opening message is the oldest loaded one: the end of an inverted list, the start of the other.
    if (IS_MESSAGE_LIST_INVERTED) {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    } else {
      scrollToStart();
    }
  }, [scrollToOpeningRequest, conversation.id, scrollToStart]);

  const { track: trackChatEvent } = useChatAnalytics();

  const { store: addReferencesStore, submitUrls: submitAddReferencesUrls } = useAddReferences({
    conversationId: conversation.id,
  });

  const addReferencesSessionStatus = useStore(addReferencesStore, (s) => s.status);

  const isAddReferencesSheetOpen =
    addReferencesSessionStatus === ADD_REFERENCES_SESSION_STATUS.ENTERING_URLS ||
    addReferencesSessionStatus === ADD_REFERENCES_SESSION_STATUS.FORMATTING;

  const handleOpenProactiveReferencesSheet = useCallback(() => {
    addReferencesStore.getState().openEntry();

    trackChatEvent(ANALYTICS_EVENTS.ADD_REFERENCES_SHEET_OPENED);
  }, [addReferencesStore, trackChatEvent]);

  const handleAddReferencesCloseSheet = useCallback(() => {
    if (addReferencesStore.getState().status === ADD_REFERENCES_SESSION_STATUS.ENTERING_URLS) {
      trackChatEvent(ANALYTICS_EVENTS.ADD_REFERENCES_SHEET_CANCELED);
    }

    addReferencesStore.getState().closeEntry();
  }, [addReferencesStore, trackChatEvent]);

  const [showReferenceConfetti, setShowReferenceConfetti] = useState(false);

  /**
   * Measured height of the floating composer overlay, fed back to the message list
   * as scroll room so nothing can be stranded underneath it.
   *
   * The overlay floats precisely so messages scroll behind it rather than stopping
   * at a hard edge, but that leaves whatever sits at the end of the list covered and
   * unreachable — which took out an in-chat card an expert needs to press. Giving the
   * list this much extra scroll room means its last item can always come out from
   * under the overlay, while messages still pass behind it on the way past.
   *
   * Measured rather than derived because the height depends on which nudges are
   * showing and how far their copy wraps.
   */
  const [composerOverlayHeight, setComposerOverlayHeight] = useState(0);

  const composerFloats = useComposerFloats();

  const [composerDockHeight, setComposerDockHeight] = useState(0);

  const confettiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAddReferencesSubmit = useCallback(async () => {
    const sanitizedCount = addReferencesStore
      .getState()
      .urls.filter((url) => url.trim().length > 0).length;

    trackChatEvent(ANALYTICS_EVENTS.ADD_REFERENCES_SUBMITTED, { url_count: sanitizedCount });

    const succeeded = await submitAddReferencesUrls(formatReferencesBatch);

    if (!succeeded) {
      trackChatEvent(ANALYTICS_EVENTS.ADD_REFERENCES_FAILED, {
        url_count: sanitizedCount,
        error_message: addReferencesStore.getState().errorMessage ?? null,
      });

      return;
    }

    trackChatEvent(ANALYTICS_EVENTS.ADD_REFERENCES_SUCCEEDED, { reference_count: sanitizedCount });

    if (confettiTimerRef.current) {
      clearTimeout(confettiTimerRef.current);
    }

    confettiTimerRef.current = setTimeout(() => setShowReferenceConfetti(true), 320);
  }, [addReferencesStore, submitAddReferencesUrls, trackChatEvent]);

  useEffect(() => {
    return () => {
      if (confettiTimerRef.current) {
        clearTimeout(confettiTimerRef.current);
      }
    };
  }, []);

  const handleConfettiComplete = useCallback(() => {
    setShowReferenceConfetti(false);
  }, []);

  useChatRealtimeSync({
    conversation,
    closedConversation,
    recipient,
    parseMessage,
    scrollToTop: () => {
      if (!IS_MESSAGE_LIST_INVERTED) {
        scrollToLatest();

        return;
      }

      scrollViewRef.current?.scrollToOffset({
        offset: 0,
        animated: true,
      });
    },
    debugChatMessages,
  });

  const { colors, theme } = useColors();

  const {
    files,
    setFiles,
    noPHIConfirmed,
    setNoPHIConfirmed,
    hasFiles,
    canSendWithFiles,
    resetAttachments,
  } = useChatAttachments();

  const isPrimumAnonymousRelayConversation =
    isPrimumUser && !isAuthor && isAnonymousPrimumRoutedInquiry(inquiry);

  const askAnonymouslySide = isPrimumUser ? getAskAnonymouslySide(inquiry, { isAuthor }) : null;

  /** The sender-choice prompt belongs to the whole Ask Anonymously queue, not just anonymous threads. */
  const isPrimumAskAnonymouslyInboxConversation = askAnonymouslySide === 'inbox';

  /** The thread Ask Anonymously forwarded to an expert; staff acting as Ask Anonymously are its author. */
  const isPrimumAskAnonymouslyExpertConversation = askAnonymouslySide === 'expert';

  const { isEnabled: isMessageTemplatesEnabled } = useMessageTemplates();

  const { templateVariables, composerPlaceholder, showRelaySendPrompt } = useComposerPlaceholder({
    inquiry,
    recipient,
    isPrimumAskAnonymouslyInboxConversation,
    isPrimumAskAnonymouslyExpertConversation,
    isAnonymousInquiryGroupingEnabled,
    sendAsExpertRelay: false,
  });

  /** Templates cover every Ask Anonymously thread, on either side; they show once the API has returned templates. */
  const showTemplateAction = isMessageTemplatesEnabled && askAnonymouslySide !== null;

  const templateAudience = askAnonymouslySide === 'expert' ? 'recipient' : 'submitter';

  const sendAsPrimumSupport = isPrimumAskAnonymouslyInboxConversation;

  const {
    isSending,
    sendingStatus,
    sendMessage: originalSendMessage,
    retryMessage,
    discardMessage,
  } = useChatSendMessage({
    clearDraft,
    conversation,
    files,
    inquiryGroupsEnabled: isInboxGroupedInquiriesEnabled,
    inquiryGroupSize: inquiry.inquiryGroupSize ?? undefined,
    hasFiles,
    haptic,
    inquiryId: inquiry.id,
    inquiryParentId: inquiry.parentId,
    isPrimumAnonymousRelayConversation,
    isMaintenanceMode,
    noPHIConfirmed,
    resetAttachments,
    setDraftMessage,
    toast,
    sendAsExpertRelay: false,
    sendAsPrimumSupport,
  });

  /**
   * Wraps the real send so sending a reply while the inquiry is marked records
   * the attestation for analytics. The gratitude toast fires at toggle-on (see
   * `handleOnYourHonorPress`), not here — the persistent mark stays lit, so
   * thanking per reply would nag. Both post-send side-effects are expert-only
   * and deferred until the send promise resolves so they don't fire on a failed
   * or maintenance-mode-blocked send.
   */
  const sendMessageWithOnYourHonor = useCallback(
    (...args: Parameters<typeof originalSendMessage>) => {
      const result = originalSendMessage(...args);

      // Both side-effects are expert-only: attestation analytics only make sense
      // on the recipient's sends, and firstRespondedAt is set by the expert's
      // first reply. Deferring into .then() ensures they don't fire on a send
      // that was blocked or failed inside originalSendMessage.
      if (!isAuthor) {
        Promise.resolve(result)
          .then(() => {
            acknowledgeOnYourHonorSent();

            // The expert's first reply sets `firstRespondedAt` on the server,
            // which opens the On your honor gate. Refetch once so the toggle
            // appears without a reload. After that first reply this condition
            // never fires again.
            if (inquiry.firstRespondedAt == null) {
              return queryClient.invalidateQueries({
                queryKey: getInquiryV2QueryOptions({ conversationId: conversation.id }).queryKey,
              });
            }
          })
          .catch(() => undefined);
      }

      return result;
    },
    [
      originalSendMessage,
      acknowledgeOnYourHonorSent,
      isAuthor,
      inquiry.firstRespondedAt,
      conversation.id,
      queryClient,
    ]
  );

  const {
    showMobileSetupModal,
    showSmsBanner,
    handleSendMessageWithCheck,
    handlePhoneNumberSetup,
    handleDismissModal,
    handleBannerTurnOn,
    handleBannerDismiss,
  } = useExpertFirstMessageNotification({
    isExpert: !isAuthor,
    messages,
    conversationId: conversation.id,
    onSendMessage: sendMessageWithOnYourHonor,
  });

  /**
   * Every send path funnels through here, so the wrong-thread guard lives at
   * this choke point rather than only on the controls. While a grouped tab is
   * still resolving, the rendered conversation is the previously selected
   * sibling; sending then would deliver to that thread and look like the
   * message simply vanished.
   */
  const sendMessage = useCallback(
    async (...args: Parameters<typeof handleSendMessageWithCheck>) => {
      if (isResolvingActiveConversation) {
        // The chat library resets the composer before calling this, so refusing
        // to send is not enough — the text has to be put back or the reader
        // loses what they typed, which is the very failure this guard exists to
        // prevent.
        const [newMessages] = args;

        const clearedText = newMessages?.[0]?.text ?? '';

        if (clearedText) {
          setDraftMessage(clearedText);
        }

        Sentry.addBreadcrumb({
          category: 'chat',
          level: 'warning',
          message: 'Send suppressed while the selected grouped tab was resolving',
          data: { renderedConversationId: conversation.id },
        });

        return;
      }

      await handleSendMessageWithCheck(...args);
    },
    [conversation.id, handleSendMessageWithCheck, isResolvingActiveConversation, setDraftMessage]
  );

  /**
   * The reference-assistant intro shows only when it is the active composer
   * callout and no SMS deliverability nudge is occupying the composer. That
   * functional nudge takes precedence so two nudges never stack — the
   * announcement steps aside rather than crowd it.
   */
  const isReferenceAssistantIntroVisible = showReferenceAssistantIntro && !showSmsBanner;

  const hasTrackedReferenceAssistantIntroView = useRef(false);

  useEffect(() => {
    if (isReferenceAssistantIntroVisible && !hasTrackedReferenceAssistantIntroView.current) {
      hasTrackedReferenceAssistantIntroView.current = true;

      trackReferenceAssistantIntroViewed(referenceAssistantAnalyticsContext);
    }
  }, [isReferenceAssistantIntroVisible, referenceAssistantAnalyticsContext]);

  /**
   * Dismissing the intro resolves the callout so it stays gone across the
   * expert's devices, without earning the bonus — the bonus is earned only by
   * actually using the assistant.
   */
  const handleDismissReferenceAssistantIntro = useCallback(() => {
    trackReferenceAssistantIntroDismissed(referenceAssistantAnalyticsContext);

    dismissActiveComposerCallout();
  }, [referenceAssistantAnalyticsContext, dismissActiveComposerCallout]);

  /**
   * The callout's action opens the citation wizard (automated search) for the
   * message that has the affordance, then resolves the callout — the one-time
   * nudge has served its purpose once the expert engages with it.
   */
  const handleReferenceAssistantIntroAction = useCallback(() => {
    openReferenceAssistantWizard();

    dismissActiveComposerCallout();
  }, [openReferenceAssistantWizard, dismissActiveComposerCallout]);

  const trackAnonymousRelayInteraction = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      Promise.resolve(
        trackEvent(event, {
          inquiry_id: conversation.id,
          inquiryId: inquiry.id,
          conversationId: conversation.id,
          inquiryGroupsEnabled: isInboxGroupedInquiriesEnabled,
          inquiryParentId: inquiry.parentId ?? null,
          inquiryGroupSize: inquiry.inquiryGroupSize ?? 1,
          isGroupedInquiry: (inquiry.inquiryGroupSize ?? 1) > 1,
          isPrimumAnonymousRelayConversation,
          ...properties,
        })
      ).catch(() => undefined);
    },
    [
      conversation.id,
      inquiry.id,
      inquiry.inquiryGroupSize,
      inquiry.parentId,
      isInboxGroupedInquiriesEnabled,
      isPrimumAnonymousRelayConversation,
    ]
  );

  const handleTemplateInsert = useCallback(
    (text: string) => {
      setDraftMessage(text);
    },
    [setDraftMessage]
  );

  const handleTemplateActionOpen = useCallback(() => {
    trackAnonymousRelayInteraction(ANALYTICS_EVENTS.MESSAGE_TEMPLATE_OPENED, {
      sendMode: MESSAGE_DATA_TYPES.PRIMUM_SUPPORT,
    });
  }, [trackAnonymousRelayInteraction]);

  const handleTemplateSelected = useCallback(
    (template: MessageTemplate) => {
      trackAnonymousRelayInteraction(ANALYTICS_EVENTS.MESSAGE_TEMPLATE_SELECTED, {
        templateId: template.id,
        templateTitle: template.title,
        templateCategory: template.category,
      });
    },
    [trackAnonymousRelayInteraction]
  );

  const {
    isChoosingAttribution,
    relayExpertName,
    setRelayExpertName,
    openAttributionPrompt,
    handleComposerSend,
    handleRelaySendChoice,
    handleRelaySendCancel,
  } = useExpertRelaySend({
    sendMessage,
    showRelaySendPrompt,
    defaultExpertName: templateVariables.expert,
    draftMessage,
    setDraftMessage,
    trackInteraction: trackAnonymousRelayInteraction,
  });

  const renderToolbar = useCallback(
    (inputToolbarProps: InputToolbarProps<IMessage>) => {
      if (isChoosingAttribution) {
        return (
          <ChatToolbar>
            <ExpertRelaySendConfirm
              expertName={relayExpertName}
              onChangeExpertName={setRelayExpertName}
              onSendAsExpert={() => handleRelaySendChoice(true)}
              onSendAsPrimumSupport={() => handleRelaySendChoice(false)}
              onCancel={handleRelaySendCancel}
              isSending={isSending}
            />
          </ChatToolbar>
        );
      }

      return (
        <>
          <ChatToolbar>
            <View>
              <AttachmentAction files={files} setFiles={setFiles} isSending={isSending} />
            </View>
            <View>
              <ReferencesAction
                onPress={handleOpenProactiveReferencesSheet}
                isSending={isSending}
              />
            </View>
            {isPrimumUser && (
              <View>
                <LinkToInquiryAction
                  conversationSid={conversation.id}
                  inquiry={inquiry}
                  isSending={isSending}
                />
              </View>
            )}
            {/* Pushes the template action and send button into a right cluster. */}
            <View style={{ marginLeft: 'auto' }} />
            {showTemplateAction && (
              <View>
                <TemplateAction
                  audience={templateAudience}
                  isSending={isSending}
                  onInsert={handleTemplateInsert}
                  onOpen={handleTemplateActionOpen}
                  onSelect={handleTemplateSelected}
                  variables={templateVariables}
                />
              </View>
            )}
            <View>
              {showRelaySendPrompt ? (
                <Pressable
                  onPress={openAttributionPrompt}
                  disabled={!draftMessage || isSending || isResolvingActiveConversation}
                  hitSlop={ICON_BUTTON_HIT_SLOP}
                  className="h-12 w-12 items-center justify-center active:opacity-80"
                  {...getAccessibilityProps({
                    role: 'button',
                    label: 'Choose how to send this reply',
                  })}>
                  <Icon
                    as={SendIcon}
                    size="lg"
                    className={
                      draftMessage && !isResolvingActiveConversation
                        ? 'text-primary-500'
                        : 'text-typography-300'
                    }
                  />
                </Pressable>
              ) : (
                <ChatToolbarSendButton
                  inputToolbarProps={inputToolbarProps}
                  draftMessage={draftMessage}
                  canSendWithFiles={canSendWithFiles}
                  isMaintenanceMode={isMaintenanceMode}
                  isSending={isSending}
                  sendingStatus={sendingStatus}
                  toast={toast}
                  noPHIConfirmed={noPHIConfirmed}
                  hasFiles={hasFiles}
                  isResolvingActiveConversation={isResolvingActiveConversation}
                />
              )}
            </View>
          </ChatToolbar>
        </>
      );
    },
    [
      canSendWithFiles,
      conversation.id,
      draftMessage,
      handleOpenProactiveReferencesSheet,
      handleTemplateActionOpen,
      handleTemplateInsert,
      handleTemplateSelected,
      hasFiles,
      inquiry,
      isAuthor,
      isMaintenanceMode,
      isPrimumUser,
      isSending,
      noPHIConfirmed,
      files,
      handleRelaySendChoice,
      handleRelaySendCancel,
      isChoosingAttribution,
      openAttributionPrompt,
      relayExpertName,
      sendingStatus,
      setFiles,
      showRelaySendPrompt,
      showTemplateAction,
      templateVariables,
      toast,
    ]
  );

  const renderMessageText = useMemo(() => {
    return createRenderMessageText({
      outgoingTextColor: colors.white,
      incomingTextColor: theme.colors.text,
      conversationId: conversation.id,
      recipientId: inquiry.recipient?.id,
    });
  }, [colors.white, theme.colors.text, conversation.id, inquiry.recipient?.id]);

  const renderTime = useMemo(() => createRenderTime(), []);

  const showError = toast.showError;

  const statementReferences = useStatementReferences(conversation.id);

  const { relayTargets, relayMessage } = useMessageRelay({
    conversationId: conversation.id,
    enabled: askAnonymouslySide !== null,
  });

  /**
   * A plain long press copies the text, as it always has. On an Ask
   * Anonymously thread with somewhere to relay to, it opens a menu instead, so
   * a relay to a real person is always a deliberate choice.
   */
  const relayRef = useRef({ relayTargets, relayMessage });

  relayRef.current = { relayTargets, relayMessage };

  /**
   * Reads relay state through a ref so the handlers, and the render context
   * built from them, keep their identity when the targets refetch.
   */
  const openMessageMenu = useCallback(
    (context: unknown, message: IMessage, { menuForCopyOnly }: { menuForCopyOnly: boolean }) => {
      const actions = buildMessageLongPressActions({
        message: message as ICustomMessage,
        relayTargets: relayRef.current.relayTargets,
        onCopy: (text) => {
          copyToClipboard(text).catch(() => undefined);
        },
        onRelay: (messageSid, target) => {
          relayRef.current.relayMessage(messageSid, target).catch(() => undefined);
        },
      });

      if (actions.length === 0) {
        return false;
      }

      haptic.impactMedium();

      if (!menuForCopyOnly && actions.length === 1 && actions[0].kind === 'copy') {
        actions[0].run();

        return true;
      }

      const options = [...actions.map((action) => action.label), 'Cancel'];

      (context as IGiftedChatContext)
        .actionSheet()
        .showActionSheetWithOptions(
          { options, cancelButtonIndex: actions.length },
          (buttonIndex) => {
            if (buttonIndex !== undefined) {
              actions[buttonIndex]?.run();
            }
          }
        );

      return true;
    },
    [copyToClipboard, haptic]
  );

  const handleLongPress = useCallback(
    (context: unknown, message: IMessage) => {
      openMessageMenu(context, message, { menuForCopyOnly: false });
    },
    [openMessageMenu]
  );

  /** Web right-click: always a menu, so the browser's own is only replaced when there is something to offer. */
  const handleMessageContextMenu = useCallback(
    (context: unknown, message: IMessage) =>
      openMessageMenu(context, message, { menuForCopyOnly: true }),
    [openMessageMenu]
  );

  const renderContext = useMemo<RenderContext>(
    () => ({
      userId,
      inquiry,
      recipient,
      conversation,
      isAuthor,
      isPrimumAnonymousRelayConversation,
      isPrimumAskAnonymouslyInboxConversation,
      statementReferences,
      colors,
      closedConversation,
      showError,
      onRetrySend: retryMessage,
      onDiscardSend: discardMessage,
      onMessageContextMenu: handleMessageContextMenu,
    }),
    [
      userId,
      inquiry,
      recipient,
      conversation,
      isAuthor,
      isPrimumAnonymousRelayConversation,
      isPrimumAskAnonymouslyInboxConversation,
      statementReferences,
      colors,
      closedConversation,
      showError,
      handleMessageContextMenu,
      retryMessage,
      discardMessage,
    ]
  );

  const renderBubble = useMemo(() => makeRenderBubble(renderContext), [renderContext]);

  const renderMessage = useMemo(() => makeRenderMessage(renderContext), [renderContext]);

  const renderAvatar = useMemo(() => makeRenderAvatar(renderContext), [renderContext]);

  useEffect(() => {
    if (isObserver || params.markReadOnOpen !== '1' || params.id !== conversation.id) {
      return;
    }

    const siblingConversationIds = params.siblingConversationIds
      ? params.siblingConversationIds.split(',')
      : undefined;

    router.setParams({ markReadOnOpen: undefined, siblingConversationIds: undefined });

    Promise.resolve(markAsRead({ forceBackendSync: true, siblingConversationIds })).catch(
      () => undefined
    );
  }, [
    isObserver,
    conversation.id,
    markAsRead,
    params.id,
    params.markReadOnOpen,
    params.siblingConversationIds,
  ]);

  const isDotPhrasesEnabled = useFeatureFlag(FEATURE_FLAGS.CHAT_DOT_PHRASES);

  /**
   * Bumped when a reply draft lands in the composer. iOS sends no content-size
   * change for text set in code, so gifted-chat would keep the composer one
   * line tall with the whole reply inside it; a fresh composer measures its
   * text on mount and grows to fit, which lets the expert read the reply
   * before sending it.
   */
  const [composerRemountKey, setComposerRemountKey] = useState(0);

  const webComposerInputRef = useRef<{ focus?: () => void } | null>(null);

  const focusWebComposer = useCallback(() => {
    requestAnimationFrame(() => webComposerInputRef.current?.focus?.());
  }, []);

  const handleReplyDraftHandedOff = useCallback(() => {
    if (isWeb) {
      focusWebComposer();

      return;
    }

    setComposerRemountKey((key) => key + 1);
  }, [focusWebComposer]);

  const replyDraftShortcut = useReplyDraftShortcut({
    conversationId: conversation.id,
    isExpert: currentUser?.userType === 'expert',
    composerText: draftMessage,
    setComposerText: setDraftMessage,
    onHandedOff: handleReplyDraftHandedOff,
  });

  /** An open reply draft is the shortcut worth teaching here, so its hint wins. */
  let composerPlaceholderText = composerPlaceholder;

  const placeholderNamesSender = composerPlaceholder !== DEFAULT_COMPOSER_PLACEHOLDER;

  if (replyDraftShortcut.placeholderHint) {
    composerPlaceholderText = withDotPhraseHint(composerPlaceholder, {
      hint: replyDraftShortcut.placeholderHint,
      namesSender: placeholderNamesSender,
    });
  } else if (isDotPhrasesEnabled && currentUser?.userType === 'expert') {
    composerPlaceholderText = withDotPhraseHint(composerPlaceholder, {
      namesSender: placeholderNamesSender,
    });
  }

  const { menu: dotPhraseMenu, handleKeyPress: handleDotPhraseKeyPress } = useDotPhraseMenu({
    enabled: isDotPhrasesEnabled,
    actionPhrases: replyDraftShortcut.actionPhrases,
    userId,
    isExpert: currentUser?.userType === 'expert',
    draftMessage,
    setDraftMessage,
    focusComposer: isWeb ? focusWebComposer : undefined,
  });

  /**
   * One object for the life of the screen: `WebComposer` re-attaches its textarea
   * ref whenever `textInputProps` changes identity, so an inline literal would
   * detach and reattach it on every render.
   */
  const webComposerInputProps = useMemo(
    () => ({
      onKeyPress: handleDotPhraseKeyPress,
      ref: webComposerInputRef,
      testID: COMPOSER_INPUT_TEST_ID,
    }),
    [handleDotPhraseKeyPress]
  );

  const handleInputTextChanged = useCallback(
    (text: string) => {
      setDraftMessage(text);
    },
    [setDraftMessage]
  );

  const resolvedKeyboardVerticalOffset =
    (keyboardVerticalOffset ??
      Platform.select({
        ios: 0,
        android: 52,
        default: 0,
      })) + bannerHeight;

  /**
   * First-time introduction shown above the composer, teaching the toggle once.
   * Only the banner's own dismiss (X) resolves the callout, which then stays gone
   * across the expert's devices. Marking the inquiry deliberately does NOT dismiss
   * it — teaching and using are allowed to coexist.
   */
  const onYourHonorIntroBanner = showOnYourHonorIntro ? (
    <ComposerContextBanner
      layout="stack"
      badge={ON_YOUR_HONOR_COPY.introBadge}
      tone="info"
      onDismiss={handleDismissOnYourHonorIntro}
      message={
        // The read-only toggle replica is a real Button (a View), which can't nest
        // inside Text and flow inline on native. To get the pill to sit inline and
        // have the sentence wrap around it, every word is its own Text flex item in
        // a wrapping row, with the pill as one more item — so the line breaks fall
        // between words exactly as inline text would. The row carries one combined
        // a11y label so screen readers read the sentence whole, not word by word.
        <HStack
          className="flex-wrap items-center gap-x-1 gap-y-0.5"
          accessible
          {...getAccessibilityProps({
            label: `${ON_YOUR_HONOR_COPY.introLeadIn} ${ON_YOUR_HONOR_COPY.action} ${ON_YOUR_HONOR_COPY.introMessage}`,
          })}>
          <Text size="sm" className="text-typography-800">
            {ON_YOUR_HONOR_COPY.introLeadIn}
          </Text>
          <OnYourHonorInlineBadge />
          {ON_YOUR_HONOR_COPY.introMessage.split(' ').map((word, index) => (
            <Text key={`${word}-${index}`} size="sm" className="text-typography-800">
              {word}
            </Text>
          ))}
        </HStack>
      }
    />
  ) : null;

  /**
   * First-time announcement of the reference assistant and the one-time doubled
   * honorarium. Dismiss-only by design — experts add references through the
   * existing entry points (toolbar, "Primum found these references" prompt), not
   * from the banner. Dismiss resolves the callout, and actually using the
   * assistant earns the bonus and resolves it too. Mutually exclusive with the
   * On-your-honor intro via the composer-callout coordinator, so both can occupy
   * the same slot.
   */
  const referenceAssistantIntroBanner = isReferenceAssistantIntroVisible ? (
    <ComposerContextBanner
      layout="stack"
      badge={REFERENCE_ASSISTANT_INTRO_COPY.badge}
      tone="info"
      dismissAccessibilityLabel={REFERENCE_ASSISTANT_INTRO_COPY.dismissAccessibilityLabel}
      onDismiss={handleDismissReferenceAssistantIntro}
      message={
        // The action mirrors the in-chat add-references button's styling (a
        // solid primary Button) so the callout trains the same gesture — just a
        // compact `xs` size and its own label. Rendered in the message slot so
        // it sits below the copy rather than as the banner's plain-text action.
        <VStack space="sm">
          <Text size="sm" className="text-typography-800">
            {REFERENCE_ASSISTANT_INTRO_COPY.message}
          </Text>
          <Button
            action="primary"
            variant="solid"
            size="xs"
            className="self-start"
            onPress={handleReferenceAssistantIntroAction}
            {...getAccessibilityProps({
              role: 'button',
              label: REFERENCE_ASSISTANT_INTRO_COPY.actionAccessibilityLabel,
            })}>
            <ButtonText>{REFERENCE_ASSISTANT_INTRO_COPY.actionLabel}</ButtonText>
          </Button>
        </VStack>
      }
    />
  ) : null;

  /**
   * The On-your-honor toggle. Pressing it marks the inquiry and opens the
   * post-selection reason sheet; the reason capture lives in that sheet, not
   * inline here.
   */
  const onYourHonorControl = (
    <OnYourHonorAction
      isConfirmed={isOnYourHonorConfirmed}
      isSending={isSending}
      onPress={handleOnYourHonorPress}
    />
  );

  /**
   * Inquiry-level actions sit above the composer (not in the message toolbar):
   * On your honor today, Leave feedback (scaffold, flag-gated off) next. Each
   * action self-gates and the whole row is absent when none apply (see
   * InquiryActionBar). The post-response feedback micro-survey is not an action
   * here — it pops up as a sheet after the On-your-honor mark (see below).
   */
  const inquiryActionBar =
    !isObserver && (canMarkOnYourHonor || isLeaveFeedbackEnabled) ? (
      <InquiryActionBar>
        {canMarkOnYourHonor ? onYourHonorControl : null}
        {isLeaveFeedbackEnabled ? (
          <LeaveFeedbackAction onPress={handleLeaveFeedback} isSending={isSending} />
        ) : null}
      </InquiryActionBar>
    ) : null;

  const smsBanner =
    showSmsBanner && !closedConversation ? (
      <ComposerContextBanner
        message={SMS_FIRST_MESSAGE_BANNER_COPY.message}
        actionLabel={SMS_FIRST_MESSAGE_BANNER_COPY.actionLabel}
        actionAccessibilityLabel="Turn on text notifications"
        dismissAccessibilityLabel="Dismiss text notifications reminder"
        onAction={handleBannerTurnOn}
        onDismiss={handleBannerDismiss}
      />
    ) : null;

  /**
   * The chip helps an expert start the first reply. After it, `.draft` still
   * opens a draft for a follow-up, without the chip taking room over the chat.
   */
  const draftReplyChip =
    replyDraftShortcut.chip &&
    inquiry.firstRespondedAt == null &&
    !dotPhraseMenu &&
    !isObserver &&
    !closedConversation ? (
      <DraftReplyChip
        label={replyDraftShortcut.chip.label}
        onPress={replyDraftShortcut.chip.onPress}
      />
    ) : null;

  /**
   * Every composer nudge plus the inquiry action row, floated as one group over the
   * chat so messages scroll behind them rather than stopping at a hard edge.
   *
   * Grouping is what makes the float safe from itself. Anchoring the action row alone
   * to the composer's top edge put it in the same gap a banner occupies, so the two
   * overlapped. Stacked inside one overlay they lay out in normal flow relative to
   * each other and cannot collide.
   *
   * What makes it safe for the messages underneath is the measured height, which the
   * list takes as extra scroll room. Floating opaque banners without that left the
   * end of the list permanently covered, and an in-chat card there could not be
   * pressed at all. The overlay must therefore always report its height — if this
   * measurement is ever dropped, the float has to go with it.
   *
   * The SMS nudge belongs here too. Gifted-chat's `renderChatFooter` renders inside
   * the message container, so a banner there consumes list height while these float,
   * and it would also sit under this overlay unmeasured.
   *
   * `box-none` keeps the container itself untouchable so the empty area beside the
   * right-aligned action row passes presses through to the messages underneath; only
   * the banners and buttons themselves take input.
   */
  const composerOverlay =
    dotPhraseMenu ||
    draftReplyChip ||
    smsBanner ||
    onYourHonorIntroBanner ||
    referenceAssistantIntroBanner ||
    inquiryActionBar ? (
      <View
        pointerEvents="box-none"
        onLayout={({ nativeEvent }) => {
          const measured = Math.round(nativeEvent.layout.height);

          setComposerOverlayHeight((current) => (current === measured ? current : measured));
        }}
        style={{ position: 'absolute', left: 0, right: 0, bottom: '100%' }}>
        {smsBanner}
        {onYourHonorIntroBanner}
        {referenceAssistantIntroBanner}
        {dotPhraseMenu ? <DotPhraseMenu {...dotPhraseMenu} /> : null}
        {draftReplyChip}
        {inquiryActionBar}
      </View>
    ) : null;

  const isComposerFloating = composerFloats && !closedConversation && !isObserver;

  /**
   * Derived rather than read straight from state: `onLayout` cannot fire for an
   * overlay or a floating composer that is no longer mounted, so their last measured
   * heights would otherwise linger as scroll room under nothing once every nudge is
   * dismissed or the conversation closes. These conditions must match where each one
   * renders.
   */
  const messageListBottomInset =
    (composerOverlay ? composerOverlayHeight : 0) +
    (isComposerFloating ? composerDockHeight : bottomInset);

  useEffect(() => {
    followEnd(false);
  }, [followEnd, messageListBottomInset, topInset]);

  /** The list is inverted, so `paddingTop` lands at the visual bottom, as room under the composer's overlay. */
  const invertedListProps: MessageListProps = {
    contentContainerStyle: {
      flexGrow: 1,
      justifyContent: 'flex-start',
      paddingTop: messageListBottomInset,
    },
  };

  /**
   * The list runs oldest first under the floating header, so it pads its first message clear
   * of that chrome. A short thread sits against the composer because the list header grows to
   * fill the free space; `justifyContent` on the content container does not move a virtualized
   * list's cells. The room under the composer's overlay is the footer rather than padding,
   * since the list's scroll to the end ignores the content container's padding. An older page
   * loads on its own when the reader nears the top, with a spinner in the header, and keeps the
   * message they were reading in place.
   */
  const nativeListProps: MessageListProps = {
    ...followEndHandlers,
    contentContainerStyle: {
      flexGrow: 1,
      paddingTop: topInset + MESSAGE_LIST_EDGE_PADDING,
      paddingHorizontal: MESSAGE_LIST_SIDE_PADDING,
    },
    ListHeaderComponent: isFetchingNextPage ? (
      <HStack className="my-4 justify-center">
        <Spinner size="small" />
      </HStack>
    ) : (
      <View />
    ),
    ListHeaderComponentStyle: { flexGrow: 1 },
    scrollIndicatorInsets: { top: topInset, bottom: messageListBottomInset },
    contentInsetAdjustmentBehavior: 'never',
    maintainVisibleContentPosition: { minIndexForVisible: 0 },
    onStartReached: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage().catch(() => undefined);
      }
    },
    onStartReachedThreshold: 0.5,
  };

  /**
   * The same skeleton the screen shows while the conversation loads, kept over the list and the
   * composer until the first page of messages arrives, so the reader sees one placeholder from
   * tapping the inquiry to its thread rather than one skeleton handing over to another. It also
   * fills the message list's first layout pass, when the list draws nothing at all, but only
   * while the cover is up: the list takes that pass on every mount, and a chat whose messages
   * are cached would otherwise flash a skeleton each time it opened. The cover stays until the
   * list has laid out the messages and settled at its end, then fades, so the reader sees the
   * finished thread appear rather than watch it scroll into place.
   */
  const loadingSkeleton = (
    <ChatScreenSkeleton
      showComposer={!closedConversation && !isObserver}
      showActionBar={inquiryActionBar != null}
      bottomInset={bottomInset}
    />
  );

  const listViewProps: MessageListProps = {
    keyboardShouldPersistTaps: 'handled',
    keyboardDismissMode: 'on-drag',
    showsVerticalScrollIndicator: false,
    style: { flex: 1 },
    onScroll: measureCaseBox,
    scrollEventThrottle: 100,
    ...(IS_MESSAGE_LIST_INVERTED ? invertedListProps : nativeListProps),
    onContentSizeChange: (width: number, height: number) => {
      (IS_MESSAGE_LIST_INVERTED ? invertedListProps : nativeListProps).onContentSizeChange?.(
        width,
        height
      );

      notifyListLayoutChange();
    },
  };

  return (
    <KeyboardAvoidingView
      behavior="padding"
      className="h-full w-full flex-1"
      keyboardVerticalOffset={resolvedKeyboardVerticalOffset}>
      <View
        ref={listViewportRef}
        collapsable={false}
        style={{ flex: 1, paddingHorizontal: isWeb ? MESSAGE_LIST_SIDE_PADDING : 0 }}>
        <GiftedChat
          messages={listMessages}
          messageContainerRef={scrollViewRef as React.RefObject<AnimatedList<IMessage>>}
          user={{
            _id: userId,
          }}
          renderDay={renderDay}
          showAvatarForEveryMessage={isAnonymousPrimumRoutedInquiry(inquiry)}
          inverted={IS_MESSAGE_LIST_INVERTED}
          infiniteScroll={IS_MESSAGE_LIST_INVERTED}
          loadEarlier={hasNextPage}
          onLoadEarlier={fetchNextPage}
          isLoadingEarlier={isLoading}
          isKeyboardInternallyHandled={false}
          disableComposer={
            conversationStatus !== 'active' || isSending || isResolvingActiveConversation
          }
          minComposerHeight={resolvedMinComposerHeight}
          maxComposerHeight={maxComposerHeight}
          listViewProps={listViewProps}
          renderLoading={() => (isLoadingCovered ? loadingSkeleton : null)}
          shouldUpdateMessage={shouldUpdateMessageFiles}
          renderBubble={renderBubble}
          renderMessage={renderMessage}
          renderAvatar={renderAvatar}
          renderMessageText={renderMessageText}
          renderTime={renderTime}
          renderFooter={() => (
            <>
              <ChatContentFooter
                closedConversation={closedConversation}
                isAuthor={isAuthor}
                recipient={recipient}
                files={files}
                setFiles={setFiles}
                isSending={isSending}
                noPHIConfirmed={noPHIConfirmed}
                setNoPHIConfirmed={setNoPHIConfirmed}
              />
              {IS_MESSAGE_LIST_INVERTED ? null : (
                <View style={{ height: messageListBottomInset + MESSAGE_LIST_EDGE_PADDING }} />
              )}
            </>
          )}
          renderInputToolbar={(props) => {
            if (closedConversation || isObserver) {
              return null;
            }

            if (isWeb) {
              return (
                <View style={{ marginBottom: 12 }}>
                  <View style={{ position: 'relative' }}>
                    {composerOverlay}
                    <View
                      style={{
                        borderWidth: 1,
                        borderColor: colors.outline[300],
                        borderRadius: COMPOSER_CORNER_RADIUS,
                        overflow: 'hidden',
                        paddingTop: 0,
                        marginTop: 0,
                        marginBottom: 0,
                        paddingBottom: 6,
                        backgroundColor: colors.background[0],
                        ...((conversationStatus !== 'active' || isSending) && {
                          backgroundColor: colors.background[50],
                        }),
                      }}>
                      <WebComposer
                        {...(props as unknown as import('react-native-gifted-chat').ComposerProps)}
                        isSending={isSending}
                        textInputProps={webComposerInputProps}
                        placeholderTextColor={colors.typography[400]}
                        textInputStyle={{
                          ...BASE_INPUT_STYLES,
                          flex: 0,
                          flexBasis: 'auto',
                          color: theme.colors.text,
                          maxHeight: maxComposerHeight,
                          overflow: 'auto' as TextStyle['overflow'],
                          marginLeft: 0,
                          marginRight: 0,
                          marginTop: 0,
                          marginBottom: 0,
                          paddingLeft: 12,
                          paddingRight: 12,
                          paddingTop: 4,
                          paddingBottom: 4,
                        }}
                        placeholder={composerPlaceholderText}
                      />
                      {renderToolbar(props)}
                    </View>
                  </View>
                </View>
              );
            }

            const isComposerInactive =
              conversationStatus !== 'active' || isSending || isResolvingActiveConversation;

            return (
              <NativeComposerDock
                overlay={composerOverlay}
                horizontalPadding={MESSAGE_LIST_SIDE_PADDING}
                bottomInset={isKeyboardVisible ? 0 : bottomInset}
                inFlowBottomGap={isKeyboardVisible ? NATIVE_COMPOSER_KEYBOARD_GAP : 0}
                isDisabled={isComposerInactive}
                onFloatingHeightChange={(measured) =>
                  setComposerDockHeight((current) => (current === measured ? current : measured))
                }>
                <InputToolbar
                  {...props}
                  containerStyle={{
                    borderTopWidth: 0,
                    backgroundColor: 'transparent',
                  }}
                  primaryStyle={
                    composerFloats
                      ? { margin: 0 }
                      : {
                          margin: 0,
                          borderWidth: 1,
                          borderBottomWidth: 0,
                          borderColor: colors.outline[300],
                          ...composerPrimaryCornerStyle,
                          ...(isComposerInactive && { backgroundColor: colors.background[50] }),
                        }
                  }
                  accessoryStyle={
                    composerFloats
                      ? { height: 'auto' as unknown as number }
                      : {
                          height: 'auto' as unknown as number,
                          borderWidth: 1,
                          borderTopWidth: 0,
                          borderColor: colors.outline[300],
                          ...composerAccessoryCornerStyle,
                          ...(isComposerInactive && { backgroundColor: colors.background[50] }),
                        }
                  }
                  renderComposer={(composerProps) => (
                    <Composer
                      key={composerRemountKey}
                      {...composerProps}
                      placeholderTextColor={colors.typography[400]}
                      textInputProps={{
                        ...composerProps.textInputProps,
                        testID: COMPOSER_INPUT_TEST_ID,
                        style: {
                          ...BASE_INPUT_STYLES,
                          color: theme.colors.text,
                          minHeight: resolvedMinComposerHeight,
                          maxHeight: maxComposerHeight,
                          textAlignVertical: 'top',
                          padding: 4,
                          margin: 0,
                        },
                      }}
                      placeholder={composerPlaceholderText}
                    />
                  )}
                  renderSend={() => <></>}
                  renderAccessory={() => renderToolbar(props)}
                />
              </NativeComposerDock>
            );
          }}
          text={draftMessage}
          onInputTextChanged={handleInputTextChanged}
          onSend={handleComposerSend}
          onLongPress={handleLongPress}
          renderLoadEarlier={(props) =>
            hasNextPage ? (
              <HStack className="my-4 justify-center">
                <Button
                  variant="link"
                  size="sm"
                  onPress={props.onLoadEarlier}
                  disabled={props.isLoadingEarlier}
                  className="px-2">
                  {props.isLoadingEarlier ? (
                    <>
                      <Spinner size="small" className="mr-2 inline-block align-middle" />
                      <ButtonText>Loading…</ButtonText>
                    </>
                  ) : (
                    <ButtonText>Load earlier messages</ButtonText>
                  )}
                </Button>
              </HStack>
            ) : null
          }
        />
        {isWeb ? null : (
          <ClinicalCaseJumpChip conversationId={conversation.id} topInset={topInset} />
        )}
        <ClinicalCasePeek conversationId={conversation.id} topInset={topInset} />
        {isLoadingCovered ? (
          <Animated.View
            pointerEvents={isLoadingCoverFading ? 'none' : 'auto'}
            style={{ ...StyleSheet.absoluteFill, opacity: loadingCoverOpacity }}>
            <View className="flex-1 bg-background-0">{loadingSkeleton}</View>
          </Animated.View>
        ) : null}
      </View>

      <AddReferencesUrlSheet
        store={addReferencesStore}
        isOpen={isAddReferencesSheetOpen}
        onClose={handleAddReferencesCloseSheet}
        onSubmit={handleAddReferencesSubmit}
      />

      <ExpertMobileNumberSetupModal
        isOpen={showMobileSetupModal}
        source="first_message"
        onClose={handleDismissModal}
        onPhoneNumberSet={handlePhoneNumberSetup}
      />

      <OnYourHonorReasonSheet
        isOpen={isReasonSheetOpen}
        thanksLine={reasonThanksLine}
        onSelectReason={handleOnYourHonorReasonChip}
        onCustomReason={handleOnYourHonorReasonCustom}
        onSkip={handleCloseReasonSheet}
        onThanksComplete={handleCloseReasonSheet}
      />

      {replyDraftShortcut.modal ? (
        <ReplyDraftModal
          isOpen={replyDraftShortcut.modal.isOpen}
          conversationId={conversation.id}
          onClose={replyDraftShortcut.close}
        />
      ) : null}

      {showReferenceConfetti ? <ConfettiBurst onComplete={handleConfettiComplete} /> : null}
    </KeyboardAvoidingView>
  );
});
